import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { AlertTriangle, Download, Printer, X } from 'lucide-react';
import useDialog from '../../hooks/useDialog';
import ClinicLogo from '../ClinicLogo';
import { pageMarginBoxes } from '../../utils/printDocument';

// The sheet that goes on the wall, and the vector file that goes to a banner printer.
//
// The code is a plain link to the complaint page, not a token, so one printed poster works for ever.
// It is drawn from the browser's own origin: only the browser knows the address staff and patients
// actually reach the hospital on. That is also the danger — see the warning below.

/**
 * The short route, served by nginx from the same file. Every character in the address becomes
 * modules in the code: /complaint.html needs a 37x37 grid, /c needs 33x33, so at a given printed
 * width each square comes out about 12% bigger and the code scans from correspondingly further
 * away. That is the whole difference on a banner read from across a room.
 *
 * /complaint.html still works, so posters already on the wall keep scanning.
 */
export const posterUrl = () => `${window.location.origin}/c`;

/**
 * The quiet zone is part of the symbol, not decoration: the QR spec wants FOUR blank modules on
 * every side and a scanner uses them to find the edges. This used to be 1, which worked only
 * because the white poster around the image happened to supply the rest. Hand that same image to a
 * designer who lays it on a coloured banner and the real quiet zone becomes one module, which is
 * where scanning starts failing at an angle or in poor light.
 */
const QUIET_ZONE_MODULES = 4;

/** Heavy correction: a poster is scanned in bad light, from an angle, and after somebody tapes it. */
const QR_OPTIONS = { margin: QUIET_ZONE_MODULES, errorCorrectionLevel: 'H' };

/**
 * A printed banner cannot be corrected later, so an address that only works inside the building is
 * worse than no poster at all. Flags the origins that are not the public site: a bare IP (QA), a
 * port, localhost.
 */
export const originLooksInternal = () => {
  const { hostname, port } = window.location;
  return (
    /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname) ||
    hostname === 'localhost' ||
    hostname.endsWith('.local') ||
    Boolean(port)
  );
};

const ComplaintPoster = ({ onClose }) => {
  const [qr, setQr] = useState(null);
  const dialogRef = useDialog(() => onClose(), { confirmDiscard: false });
  const internal = originLooksInternal();

  useEffect(() => {
    let live = true;
    QRCode.toDataURL(posterUrl(), { ...QR_OPTIONS, width: 1000 })
      .then((url) => { if (live) setQr(url); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  // What a banner printer actually wants. An SVG is drawn from the module grid at whatever size the
  // press runs, so the squares stay hard-edged at any width; the PNG above is fixed at 1000 pixels
  // and a press asked to blow that up to a metre has nothing but interpolation to do it with.
  const downloadVector = async () => {
    const svg = await QRCode.toString(posterUrl(), { ...QR_OPTIONS, type: 'svg' });
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = 'complaints-qr.svg';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  };

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 print:static print:bg-white print:p-0" role="dialog" aria-modal="true" aria-label="Complaints poster">
      {/* No browser date, title or web address on a poster for the wall. */}
      <style>{`@media print { @page { ${pageMarginBoxes({ numbered: false })} } }`}</style>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[94vh] flex flex-col print:max-w-none print:shadow-none print:max-h-none">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-center justify-between rounded-t-2xl print:hidden">
          <h2 className="text-lg font-bold text-white">Poster for the wall</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto print:p-0 print:overflow-visible">
          {internal && (
            <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 flex gap-2 print:hidden">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">This code points at a test address.</p>
                <p className="mt-0.5">
                  It would send patients to <span className="font-mono text-xs">{window.location.origin}</span>, which only
                  works inside the hospital network. Print the poster from the live site instead, or a banner will carry
                  the wrong address for as long as it hangs there.
                </p>
              </div>
            </div>
          )}

          {/* The poster itself. Everything inside is sized in points so it prints the same from any
              screen, and the whole block is what the print stylesheet keeps. */}
          <div id="complaint-poster" className="border border-gray-200 rounded-xl p-8 text-center print:border-0 print:rounded-none print:p-10">
            <div className="flex justify-center mb-4">
              <ClinicLogo size={84} />
            </div>
            <p className="urdu-text text-2xl font-semibold text-gray-900" dir="rtl" style={{ lineHeight: 2.6 }}>
              اپنی شکایت درج کروائیں
            </p>
            <p className="urdu-text text-base text-gray-800 mt-3" dir="rtl" style={{ lineHeight: 2.6 }}>
              اگر عملے کے رویّے یا دوا کے بارے میں آپ کو کوئی شکایت ہے تو بلا جھجک ہمیں بتائیں۔
              آپ کی شکایت براہِ راست انتظامیہ کو بھیجی جائے گی۔
            </p>

            <div className="my-6 flex justify-center">
              {qr ? (
                <img src={qr} alt="Code to scan to send a complaint" className="w-56 h-56 print:w-64 print:h-64" />
              ) : (
                <div className="w-56 h-56 bg-gray-50 rounded-lg" aria-hidden="true" />
              )}
            </div>

            <p className="urdu-text text-lg text-gray-900" dir="rtl" style={{ lineHeight: 2.6 }}>
              اپنے فون کے کیمرے سے یہ کوڈ اسکین کریں
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Scan this code with your phone camera to send a complaint.<br />
              It goes straight to the management.
            </p>
          </div>

          {/* Printing it bigger than a sheet of paper is a different job, so say what the press needs. */}
          <details className="mt-4 rounded-xl border border-gray-200 p-4 print:hidden">
            <summary className="text-sm font-semibold text-gray-900 cursor-pointer">
              Printing it as a banner or flex
            </summary>
            <div className="mt-3 text-sm text-gray-600 space-y-2">
              <p>
                A bigger code is easier to scan, not harder: it can be read from further away. Reckon on a scanning
                distance of about <span className="font-medium text-gray-900">ten times the width of the code</span>, so a
                30 cm code is comfortable from 3 m and a 60 cm one from 6 m. There is no size that is too big.
              </p>
              <p>
                Give the printer the <span className="font-medium text-gray-900">SVG</span> below rather than a photograph
                of this screen. It is drawn fresh at whatever size the press runs, so the squares stay sharp; the image on
                this page is a fixed 1000 pixels and goes soft when it is blown up.
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Keep the white border that comes with the file. A scanner uses it to find the edges.</li>
                <li>Ask for a matte finish. Glossy flex throws glare from ceiling lights straight back at the camera.</li>
                <li>Dark code on a light background, never the other way round, and never over a photograph.</li>
                <li>Hang it where somebody can stand square in front of it, not high up at a steep angle.</li>
              </ul>
            </div>
          </details>
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex justify-between items-center gap-2 rounded-b-2xl print:hidden">
          <span className="text-xs text-gray-500 truncate" title={posterUrl()}>{posterUrl()}</span>
          <div className="flex gap-2 flex-shrink-0">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">Close</button>
            <button type="button" onClick={downloadVector} className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
              <Download className="w-4 h-4" aria-hidden="true" /> SVG for the printer
            </button>
            <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium">
              <Printer className="w-4 h-4" aria-hidden="true" /> Print
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplaintPoster;
